import { HttpClient } from '@angular/common/http';
import { Injectable, InjectionToken, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import type {
  MarkUpsalePaymentRequest,
  UpsaleBillDto,
  UpsalePrintStatus,
  UpsalePromoResult,
} from '@lite-workspace/dto-fotoclass-book';

/** Core refuses a payment without a key; one key per dialog open makes a retry land once. */
export type SalesPaymentRequest = MarkUpsalePaymentRequest & { idempotencyKey: string };

const billUrl = (projectId: string, personId: string): string =>
  `/api/projects/${encodeURIComponent(projectId)}/persons/${encodeURIComponent(personId)}/bill`;

/** Bills of every student of one project (photographer view). */
export interface SalesDataPort {
  list(projectId: string): Promise<UpsaleBillDto[]>;
  pay(projectId: string, personId: string, req: SalesPaymentRequest): Promise<UpsaleBillDto>;
  /** Money the organizer handed to the photographer, whole rubles. */
  /** `idempotencyKey`: one per dialog open, a retry after a lost answer records the handover once; empty `note` is not sent. */
  handover(projectId: string, amount: number, idempotencyKey: string, note?: string): Promise<void>;
  /** Photographer discount of one bill, whole rubles. */
  setDiscount(projectId: string, personId: string, discount: number): Promise<UpsaleBillDto>;
  /** Promo code on one bill; a refused code = `applied: false` + reason. */
  applyPromo(projectId: string, personId: string, code: string): Promise<UpsalePromoResult>;
  /** Base position of one bill = this project package, `null` = the base album; 409 once the base is paid. */
  setTariff(projectId: string, personId: string, tariff: string | null): Promise<UpsaleBillDto>;
  /** All or nothing; count = positions moved. */
  setPrintStatus(projectId: string, positionIds: string[], status: UpsalePrintStatus): Promise<{ count: number }>;
  /** Inbox reminder to every debtor with an account; count = students it reached. */
  remind(projectId: string): Promise<{ count: number }>;
}

@Injectable({ providedIn: 'root' })
export class HttpSalesData implements SalesDataPort {
  private readonly http = inject(HttpClient);

  list(projectId: string): Promise<UpsaleBillDto[]> {
    return firstValueFrom(this.http.get<UpsaleBillDto[]>(`/api/projects/${encodeURIComponent(projectId)}/upsale/bills`));
  }

  pay(projectId: string, personId: string, req: SalesPaymentRequest): Promise<UpsaleBillDto> {
    const url = `/api/projects/${encodeURIComponent(projectId)}/persons/${encodeURIComponent(personId)}/bill/payments`;
    return firstValueFrom(this.http.post<UpsaleBillDto>(url, req));
  }

  async handover(projectId: string, amount: number, idempotencyKey: string, note?: string): Promise<void> {
    const url = `/api/projects/${encodeURIComponent(projectId)}/upsale/handover`;
    await firstValueFrom(this.http.post(url, { amount, idempotencyKey, ...(note ? { note } : {}) }));
  }

  setDiscount(projectId: string, personId: string, discount: number): Promise<UpsaleBillDto> {
    return firstValueFrom(this.http.patch<UpsaleBillDto>(billUrl(projectId, personId), { discount }));
  }

  applyPromo(projectId: string, personId: string, code: string): Promise<UpsalePromoResult> {
    return firstValueFrom(this.http.post<UpsalePromoResult>(`${billUrl(projectId, personId)}/promo`, { code }));
  }

  setTariff(projectId: string, personId: string, tariff: string | null): Promise<UpsaleBillDto> {
    return firstValueFrom(this.http.patch<UpsaleBillDto>(billUrl(projectId, personId), { tariff }));
  }

  setPrintStatus(projectId: string, positionIds: string[], status: UpsalePrintStatus): Promise<{ count: number }> {
    const url = `/api/projects/${encodeURIComponent(projectId)}/upsale/print-status`;
    return firstValueFrom(this.http.patch<{ count: number }>(url, { positionIds, status }));
  }

  remind(projectId: string): Promise<{ count: number }> {
    return firstValueFrom(this.http.post<{ count: number }>(`/api/projects/${encodeURIComponent(projectId)}/upsale/remind`, {}));
  }
}

export const SALES_DATA = new InjectionToken<SalesDataPort>('SALES_DATA', {
  providedIn: 'root',
  factory: (): SalesDataPort => inject(HttpSalesData),
});
